import * as React from 'react'
import { Box, Container, Link as MuiLink, Snackbar, Alert, Typography } from '@mui/material'
import { Link } from 'gatsby'
import Layout from '../../../../components/layout'
import Seo from '../../../../components/seo'
import AceBrandLockup from '../../../../components/ace/AceBrandLockup'
import AceGongForm from '../../../../components/ace/AceGongForm'
import AceGongButton from '../../../../components/ace/AceGongButton'
import { usePostHog } from '../../../../hooks/usePostHog'
import { aceColors, aceConvention } from '../../../../data/aceConvention'

const analyticsBase = {
  campaign: aceConvention.campaign,
  page: 'ace_gong',
}

const randomHitBudget = () => Math.floor(Math.random() * 5) + 1

const AceGongPage = () => {
  const { capture } = usePostHog()
  const [phase, setPhase] = React.useState('gate') // 'gate' | 'gong'
  const [visitor, setVisitor] = React.useState(null)
  const [hitsLeft, setHitsLeft] = React.useState(0)
  const [showMaxNotice, setShowMaxNotice] = React.useState(false)
  const [resetNotice, setResetNotice] = React.useState(false)

  React.useEffect(() => {
    capture('ace_gong_view', analyticsBase)
  }, [capture])

  const unlockGong = (visitorData) => {
    const budget = randomHitBudget()
    setVisitor(visitorData)
    setHitsLeft(budget)
    setPhase('gong')
    setResetNotice(false)
    capture('ace_gong_unlocked', { ...analyticsBase, hit_budget: budget })
  }

  const handleExhausted = () => {
    setShowMaxNotice(true)
    capture('ace_gong_exhausted', analyticsBase)
    window.setTimeout(() => {
      setPhase('gate')
      setVisitor(null)
      setHitsLeft(0)
      setResetNotice(true)
    }, 1600)
  }

  return (
    <Layout pageTitle="Ring the Workload Gong">
      <Box
        sx={{
          position: 'relative',
          left: '50%',
          right: '50%',
          ml: '-50vw',
          mr: '-50vw',
          width: '100vw',
          minHeight: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 72px)' },
          background: `linear-gradient(165deg, ${aceColors.navy} 0%, ${aceColors.navyMid} 55%, #0d2847 100%)`,
          color: '#fff',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          py: { xs: 4, md: 6 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            opacity: 0.07,
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
            backgroundSize: '48px 48px',
            pointerEvents: 'none',
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: -80,
            right: -60,
            width: 280,
            height: 280,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${aceColors.blue}55 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: -100,
            left: -40,
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${aceColors.red}33 0%, transparent 70%)`,
            pointerEvents: 'none',
          }}
        />

        <Container maxWidth="sm" sx={{ position: 'relative', px: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <AceBrandLockup light sx={{ mb: 3 }} />

          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 800,
              letterSpacing: 2,
              fontSize: { xs: '0.7rem', sm: '0.8rem' },
              textTransform: 'uppercase',
              color: aceColors.blueSoft,
              mb: 3,
            }}
          >
            ACE Handyman Services Convention
          </Typography>

          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
            {phase === 'gate' ? (
              <AceGongForm
                resetNotice={resetNotice}
                onFormStart={() => capture('ace_gong_form_start', analyticsBase)}
                onFormSubmit={(v) => capture('ace_gong_form_submit', { ...analyticsBase, store: v.storeLocation })}
                onUnlocked={unlockGong}
              />
            ) : (
              <AceGongButton
                visitor={visitor}
                remainingHits={hitsLeft}
                onHit={(left) => {
                  setHitsLeft(left)
                  capture('ace_gong_hit', { ...analyticsBase, hits_left: left })
                }}
                onExhausted={handleExhausted}
              />
            )}
          </Box>

          <Typography sx={{ textAlign: 'center', mt: 3, mb: 1 }}>
            <MuiLink
              component={Link}
              to={aceConvention.routes.landing}
              underline="hover"
              sx={{ color: 'rgba(255,255,255,0.65)', fontWeight: 600, fontSize: '0.9rem' }}
            >
              ← Back to the ACE convention page
            </MuiLink>
          </Typography>
        </Container>
      </Box>

      <Snackbar
        open={showMaxNotice}
        autoHideDuration={4000}
        onClose={() => setShowMaxNotice(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowMaxNotice(false)}
          severity="info"
          variant="filled"
          sx={{
            width: '100%',
            bgcolor: aceColors.navyMid,
            fontWeight: 700,
            boxShadow: '0 12px 32px rgba(0,0,0,0.35)',
          }}
        >
          You hit the max number of gong hits. Re-enter to play again.
        </Alert>
      </Snackbar>
    </Layout>
  )
}

export default AceGongPage

export const Head = () => (
  <>
    <Seo
      title="Ring the Workload Gong — ACE Handyman Convention"
      description="Unlock the Workload gong at the ACE Handyman Services Convention. Enter your store location, name, and email, then hit the button to ring the booth gong."
      pathname={aceConvention.routes.gong}
      image="/images/ace-handyman-services-logo.svg"
    />
    <link
      key="ace-caveat-font"
      rel="stylesheet"
      href="https://fonts.googleapis.com/css2?family=Caveat:wght@600;700&display=swap"
    />
  </>
)
